import { Stack, StackProps } from '@mui/material';
import { useRef } from 'react';

import { FOOTER_HEIGHT } from '@/components/navigation/Footer';

import { RESIZE_LINE_WIDTH, ResizeLine } from './ResizeLine';
import { SplitScreenResizeProvider } from './SplitScreenResizeContext';
import { useResizablePanel } from './useResizablePanel';

const MIN_PANEL_WIDTH = 200;

export interface SplitScreenLayoutProps {
  stackProps?: StackProps;
  leftContent: React.ReactNode;
  rightPane?: React.ReactNode;
  rightContent: React.ReactNode;
  header?: React.ReactNode;
  footer: React.ReactNode;
  /** When true, renders a draggable divider between the left and right content panels. */
  resizable?: boolean;
}

export function SplitScreenLayout({
  header,
  footer,
  leftContent,
  rightPane,
  rightContent,
  stackProps = {},
  resizable = false,
}: SplitScreenLayoutProps) {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const resizableAreaRef = useRef<HTMLDivElement | null>(null);

  // The hook always runs (Rules of Hooks), but we only use its result when
  // resizable is true. The containerRef lets the hook derive a 50% default
  // and a dynamic max width once the container is measured.
  const { panelWidth, isResizing, resizeRef, handleMouseDown } =
    useResizablePanel({
      minWidth: MIN_PANEL_WIDTH,
      minRemainingWidth: MIN_PANEL_WIDTH,
      reservedWidth: RESIZE_LINE_WIDTH,
      containerRef: resizableAreaRef,
    });

  return (
    <Stack height="100vh" width="100%" {...stackProps}>
      {/* Header Section - Fixed height */}
      {header && (
        <Stack>
          <div ref={headerRef}>{header}</div>
        </Stack>
      )}

      {/* Main Content Section - Flexible height */}
      <Stack flex={1} overflow="hidden" direction="row" minWidth={0}>
        <Stack
          ref={resizableAreaRef}
          direction="row"
          height="100%"
          flex={1}
          minWidth={0}
        >
          <Stack
            {...(resizable
              ? { width: panelWidth, flexShrink: 0 }
              : { flex: 1 })}
            minWidth={0}
            overflow="auto"
          >
            <SplitScreenResizeProvider isResizing={isResizing}>
              {leftContent}
            </SplitScreenResizeProvider>
          </Stack>
          {resizable && (
            <ResizeLine
              ref={resizeRef}
              isResizing={isResizing}
              onMouseDown={handleMouseDown}
            />
          )}
          <Stack
            flex={1}
            minWidth={resizable ? MIN_PANEL_WIDTH : 0}
            maxWidth={resizable ? undefined : '800px'}
            overflow="auto"
          >
            {rightContent}
          </Stack>
        </Stack>
        {rightPane && (
          // Chat source panels extend over the editor from this column.
          // The main content container clips them at the screen boundary.
          <Stack width="auto" flexShrink={0}>
            {rightPane}
          </Stack>
        )}
      </Stack>

      {/* Footer Section - Fixed height */}
      {footer && (
        <Stack height={FOOTER_HEIGHT} flexShrink={0}>
          {footer}
        </Stack>
      )}
    </Stack>
  );
}
